import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import SiteNavbar from "./SiteNavbar";
import SiteFooter from "./SiteFooter";
import HomeShelvesSection from "./home/HomeShelvesSection";
import HomeFeaturesSection from "./home/HomeFeaturesSection";
import HomeHowItWorksSection from "./home/HomeHowItWorksSection";
import HomeCirclesSection from "./home/HomeCirclesSection";
import { motion } from "framer-motion";
import {
  BookOpen,
  CheckCircle2,
  Coffee,
  Sparkles,
  ArrowRight,
  Check,
  Star,
  Quote,
  Users,
  ChevronRight,
  Bookmark,
} from "lucide-react";
/* ---------- Imagery ---------- */
const HERO_IMG =
  "https://images.unsplash.com/photo-1507842217343-583bb7270b66?auto=format&fit=crop&w=1600&q=80"; // warm library bookshelf

const AVATARS = [
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=200&q=80",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80",
  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=200&q=80",
];

/* ---------- Reveal wrapper ---------- */
const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98], delay }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ---------- Hero ---------- */
const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden"
      style={{ background: "var(--hero-gradient)" }}
    >
      <div className="paper-overlay absolute inset-0 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left */}
          <div className="lg:col-span-6">
            <Reveal>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs"
                style={{
                  borderColor: "var(--border)",
                  background: "var(--surface)",
                  color: "var(--text-soft)",
                }}
                data-testid="hero-eyebrow"
              >
                <Coffee
                  className="w-3.5 h-3.5"
                  style={{ color: "var(--brand)" }}
                />
                <span className="font-medium tracking-wide italic font-serif">
                  Pull up a chair, friend
                </span>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <h1
                data-testid="hero-headline"
                className="font-serif mt-7 text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.95] font-medium tracking-tight"
                style={{ color: "var(--text)" }}
              >
                Your cozy corner for{" "}
                <span
                  className="italic underline-swash"
                  style={{ color: "var(--brand)" }}
                >
                  books & friends
                </span>
                .
              </h1>
            </Reveal>

            <Reveal delay={0.2}>
              <p
                data-testid="hero-subtext"
                className="mt-8 text-lg md:text-xl leading-relaxed max-w-xl"
                style={{ color: "var(--text-soft)" }}
              >
                Lose yourself in a calm reading nook with parchment-paper pages,
                slow afternoons, and shelves of books waiting to be opened.
                Curated collections, member shelves, and weekend reads — your
                next favorite story is always one bookmark away.
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <a
                  href="https://club.pagepeers.com/register"
                  target="_blank"
                  data-testid="hero-join-btn"
                  className="group inline-flex items-center gap-2 px-7 py-4 rounded-full font-medium text-sm transition-all hover:scale-[1.02]"
                  style={{
                    background: "var(--brand)",
                    color: "var(--on-brand)",
                    boxShadow: "0 8px 24px -8px rgba(194,90,55,0.5)",
                  }}
                >
                  Join PagePeers Club
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
                <Link
                  to="/shelves"
                  data-testid="hero-browse-btn"
                  className="group inline-flex items-center gap-2 px-7 py-4 rounded-full font-medium text-sm border transition-all"
                  style={{
                    borderColor: "var(--border)",
                    color: "var(--text)",
                    background: "var(--surface)",
                  }}
                >
                  <Bookmark
                    className="w-4 h-4"
                    style={{ color: "var(--brand)" }}
                  />
                  Browse Bookshelf
                </Link>
              </div>
            </Reveal>

            {/* Stats */}
            <Reveal delay={0.45}>
              <div
                className="mt-14 grid grid-cols-3 gap-4 md:gap-8 max-w-xl pt-8 border-t"
                style={{ borderColor: "var(--border-soft)" }}
              >
                {[
                  { n: "90k+", l: "Books" },
                  { n: "23k+", l: "Reader Circles" },
                  { n: "10k+", l: "Stories Shared" },
                ].map((s) => (
                  <div
                    key={s.l}
                    data-testid={`hero-stat-${s.l.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <p
                      className="font-serif text-3xl md:text-4xl font-semibold"
                      style={{ color: "var(--text)" }}
                    >
                      {s.n}
                    </p>
                    <p
                      className="text-xs md:text-sm mt-1"
                      style={{ color: "var(--text-muted)" }}
                    >
                      {s.l}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right — hero image with floating cards */}
          <div className="lg:col-span-6 relative">
            <Reveal delay={0.2}>
              <div className="relative">
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="relative rounded-[2rem] overflow-hidden border"
                  style={{
                    borderColor: "var(--border)",
                    boxShadow: "0 30px 80px -30px rgba(120,70,40,0.35)",
                  }}
                >
                  <img
                    src={HERO_IMG}
                    alt="A warm library shelf full of books"
                    data-testid="hero-image"
                    className="w-full h-[520px] object-cover"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, transparent 40%, rgba(43,29,20,0.4) 100%)",
                    }}
                  />
                  <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3">
                    <span className="ribbon">Currently Reading</span>
                  </div>
                </motion.div>

                {/* Floating "Reading streak" card */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 }}
                  className="absolute -left-4 md:-left-8 bottom-20 rounded-2xl p-4 flex items-center gap-3 cozy-card"
                  style={{ background: "var(--surface)" }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center"
                    style={{ background: "var(--brand-tint)" }}
                  >
                    <CheckCircle2
                      className="w-5 h-5"
                      style={{ color: "var(--brand)" }}
                    />
                  </div>
                  <div>
                    <p
                      className="text-xs"
                      style={{ color: "var(--text-muted)" }}
                    >
                      Reading streak
                    </p>
                    <p
                      className="text-sm font-semibold font-serif"
                      style={{ color: "var(--text)" }}
                    >
                      42 days in a row
                    </p>
                  </div>
                </motion.div>

                {/* Floating "Circle" card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.1 }}
                  className="absolute -right-2 md:-right-6 top-16 rounded-2xl p-4 cozy-card"
                  style={{ background: "var(--surface)" }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Users
                      className="w-4 h-4"
                      style={{ color: "var(--brand)" }}
                    />
                    <span
                      className="text-xs font-semibold uppercase tracking-wider"
                      style={{ color: "var(--text-muted)" }}
                    >
                      Sunday Circle
                    </span>
                  </div>
                  <div className="flex -space-x-2">
                    {AVATARS.map((a, i) => (
                      <img
                        key={i}
                        src={a}
                        alt=""
                        className="w-7 h-7 rounded-full border-2 object-cover"
                        style={{ borderColor: "var(--surface)" }}
                      />
                    ))}
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium border-2"
                      style={{
                        background: "var(--bg-alt)",
                        borderColor: "var(--surface)",
                        color: "var(--text-soft)",
                      }}
                    >
                      +9
                    </span>
                  </div>
                </motion.div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Testimonials ---------- */
const testimonials = [
  {
    name: "Neha S.",
    role: "Weekend Reader",
    avatar: AVATARS[2],
    quote:
      "I finally track every book I own, and the community recommendations are always spot-on.",
  },
  {
    name: "Arjun M.",
    role: "Habit Builder",
    avatar: AVATARS[1],
    quote:
      "The read/unread flow is so simple my reading habit stayed consistent for months.",
  },
  {
    name: "Priya R.",
    role: "Fiction Explorer",
    avatar: AVATARS[0],
    quote:
      "Exploring other shelves helped me discover authors I would never have found otherwise.",
  },
  {
    name: "Mira H.",
    role: "Poetry Lover",
    avatar: AVATARS[2],
    quote:
      "The calm, paper-like feel makes reading time sacred again. It's my favorite app.",
  },
  {
    name: "Daniel K.",
    role: "Sunday Circle",
    avatar: AVATARS[1],
    quote:
      "Our circle of six readers has finished more books together than I did alone all last year.",
  },
];

const Testimonials = () => {
  const row = [...testimonials, ...testimonials];
  return (
    <section
      data-testid="testimonials-section"
      className="relative py-28 md:py-36 overflow-hidden"
      style={{ background: "var(--bg)" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <span
              className="inline-block text-[11px] uppercase tracking-[0.25em] font-semibold mb-4"
              style={{ color: "var(--brand)" }}
            >
              Loved by readers
            </span>
            <h2
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]"
              style={{ color: "var(--text)" }}
            >
              Stories from our{" "}
              <span className="italic" style={{ color: "var(--brand)" }}>
                reading room
              </span>
            </h2>
          </div>
        </Reveal>
      </div>

      <div className="relative">
        <div
          className="absolute left-0 top-0 bottom-0 w-32 z-10 pointer-events-none"
          style={{
            background: "linear-gradient(to right, var(--bg), transparent)",
          }}
        />
        <div
          className="absolute right-0 top-0 bottom-0 w-32 z-10 pointer-events-none"
          style={{
            background: "linear-gradient(to left, var(--bg), transparent)",
          }}
        />
        <div className="flex gap-6 animate-marquee w-max px-6">
          {row.map((t, i) => (
            <div
              key={i}
              data-testid={`testimonial-${i}`}
              className="cozy-card w-[400px] shrink-0 rounded-3xl p-8"
            >
              <Quote
                className="w-7 h-7 mb-4"
                style={{ color: "var(--brand)" }}
              />
              <p
                className="font-serif text-lg leading-relaxed italic mb-6"
                style={{ color: "var(--text)" }}
              >
                "{t.quote}"
              </p>
              <div className="flex items-center gap-3">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-11 h-11 rounded-full object-cover border"
                  style={{ borderColor: "var(--border)" }}
                />
                <div>
                  <p
                    className="font-serif text-base font-medium"
                    style={{ color: "var(--text)" }}
                  >
                    {t.name}
                  </p>
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                    {t.role}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Pricing ---------- */
const plans = [
  {
    name: "Reader",
    price: "Free",
    period: "forever",
    desc: "For quiet readers just starting a shelf.",
    features: [
      "Unlimited books on your shelf",
      "Mark read / unread",
      "Browse public shelves",
      "Join 1 reader circle",
    ],
    cta: "Start reading",
    highlighted: false,
  },
  {
    name: "Club Member",
    price: "$6",
    period: "per month",
    desc: "For readers who love company at the table.",
    features: [
      "Everything in Reader",
      "Join unlimited circles",
      "Private shelves & notes",
      "Weekly reading digest",
      "Early access to staff picks",
    ],
    cta: "Become a member",
    highlighted: true,
  },
  {
    name: "Circle Host",
    price: "$14",
    period: "per month",
    desc: "For those who curate and host their own circles.",
    features: [
      "Everything in Club Member",
      "Host unlimited circles",
      "Circle analytics & insights",
      "Custom circle theming",
      "Priority reader support",
    ],
    cta: "Host a circle",
    highlighted: false,
  },
];

const Pricing = () => {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-28 md:py-36"
      style={{ background: "var(--bg-alt)" }}
    >
      <div className="paper-overlay absolute inset-0 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span
              className="inline-block text-[11px] uppercase tracking-[0.25em] font-semibold mb-4"
              style={{ color: "var(--brand)" }}
            >
              Pricing
            </span>
            <h2
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]"
              style={{ color: "var(--text)" }}
            >
              Gentle plans for{" "}
              <span className="italic" style={{ color: "var(--brand)" }}>
                every reader
              </span>
            </h2>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {plans.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div
                data-testid={`pricing-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative rounded-3xl p-8 h-full flex flex-col ${
                  p.highlighted ? "md:scale-105" : ""
                }`}
                style={{
                  background: p.highlighted ? "var(--text)" : "var(--surface)",
                  color: p.highlighted ? "var(--bg)" : "var(--text)",
                  border: p.highlighted
                    ? "1px solid var(--text)"
                    : "1px solid var(--border)",
                  boxShadow: p.highlighted
                    ? "0 30px 60px -20px rgba(43,29,20,0.35)"
                    : "var(--card-shadow)",
                }}
              >
                {p.highlighted && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider"
                    style={{
                      background: "var(--brand)",
                      color: "var(--on-brand)",
                    }}
                  >
                    <Sparkles className="w-3 h-3" /> Most loved
                  </div>
                )}
                <h3 className="font-serif text-2xl font-medium">{p.name}</h3>
                <p
                  className="text-sm mt-2"
                  style={{
                    color: p.highlighted
                      ? "var(--on-inverse-soft)"
                      : "var(--text-soft)",
                  }}
                >
                  {p.desc}
                </p>

                <div className="mt-8 flex items-baseline gap-2">
                  <span className="font-serif text-5xl font-semibold">
                    {p.price}
                  </span>
                  <span
                    className="text-sm"
                    style={{
                      color: p.highlighted
                        ? "var(--on-inverse-soft)"
                        : "var(--text-muted)",
                    }}
                  >
                    / {p.period}
                  </span>
                </div>

                <a
                  href="#"
                  data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="mt-8 inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-full font-medium text-sm transition-all"
                  style={{
                    background: p.highlighted
                      ? "var(--brand)"
                      : "var(--bg-alt)",
                    color: p.highlighted ? "var(--on-brand)" : "var(--text)",
                  }}
                >
                  {p.cta} <ArrowRight className="w-4 h-4" />
                </a>

                <ul className="mt-8 space-y-3 flex-1">
                  {p.features.map((feat) => (
                    <li
                      key={feat}
                      className="flex items-start gap-3 text-sm"
                      style={{
                        color: p.highlighted
                          ? "var(--on-inverse-muted)"
                          : "var(--text)",
                      }}
                    >
                      <span
                        className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                        style={{
                          background: p.highlighted
                            ? "var(--on-inverse-tint)"
                            : "var(--brand-tint)",
                        }}
                      >
                        <Check
                          className="w-3 h-3"
                          style={{
                            color: p.highlighted
                              ? "var(--on-brand)"
                              : "var(--brand)",
                          }}
                          strokeWidth={3}
                        />
                      </span>
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- CTA ---------- */
const CTASection = () => {
  return (
    <section
      id="cta"
      data-testid="cta-section"
      className="relative py-24 md:py-32"
      style={{ background: "var(--bg)" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <div
            className="relative overflow-hidden rounded-[2.5rem] p-12 md:p-20 text-center paper-overlay"
            style={{
              background:
                "linear-gradient(135deg, var(--bg-alt) 0%, var(--brand-tint) 100%)",
              border: "1px solid var(--border)",
            }}
          >
            <div className="relative">
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs mb-6 font-serif italic"
                style={{
                  borderColor: "var(--border)",
                  background: "var(--surface)",
                  color: "var(--text-soft)",
                }}
              >
                <Coffee
                  className="w-3.5 h-3.5"
                  style={{ color: "var(--brand)" }}
                />
                Join the club
              </div>
              <h2
                className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-3xl mx-auto"
                style={{ color: "var(--text)" }}
              >
                Your next favorite book is waiting on{" "}
                <span className="italic" style={{ color: "var(--brand)" }}>
                  someone's shelf
                </span>
                .
              </h2>
              <p
                className="mt-6 text-lg max-w-xl mx-auto"
                style={{ color: "var(--text-soft)" }}
              >
                Start your personal collection today and become part of a warm
                community that reads, shares, and grows together.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://club.pagepeers.com/register"
                  target="_blank"
                  data-testid="cta-join-btn"
                  className="group inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium text-sm transition-all hover:scale-[1.02]"
                  style={{
                    background: "var(--brand)",
                    color: "var(--on-brand)",
                    boxShadow: "0 10px 30px -8px rgba(194,90,55,0.5)",
                  }}
                >
                  Join PagePeers Club
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
                <Link
                  to="/shelves"
                  data-testid="cta-browse-btn"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium text-sm border transition-all"
                  style={{
                    borderColor: "var(--text)",
                    color: "var(--text)",
                    background: "transparent",
                  }}
                >
                  Browse bookshelf
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------- Main ---------- */
const LandingPage = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [hash]);

  return (
    <div
      data-testid="landing-page"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <SiteNavbar />
      <Hero />
      <HomeShelvesSection Reveal={Reveal} />
      <HomeFeaturesSection Reveal={Reveal} />
      <HomeHowItWorksSection Reveal={Reveal} />
      <HomeCirclesSection Reveal={Reveal} />
      <Testimonials />
      {/* <Pricing /> */}
      <CTASection />
      <SiteFooter />
    </div>
  );
};

export default LandingPage;
